import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import PhotoLightbox from './PhotoLightbox';
import Toggle from './Toggle';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');

function SubscriptionCatalogTab({ showToast, createTrigger, onCreateHandled }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [pendingImages, setPendingImages] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    is_active: true,
  });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [modalLightboxIndex, setModalLightboxIndex] = useState(null);
  const fileInputRef = useRef(null);

  const handleDelete = (item) => {
    setConfirmDialog({
      title: 'Удалить карточку',
      message: `Удалить «${item.name}» без возможности восстановления? Действие необратимо.`,
      confirmText: 'Удалить',
      onConfirm: async () => {
        try {
          await api.deleteSubscriptionCatalogItem(item.id);
          showToast('Карточка удалена');
          resetForm();
          loadData();
        } catch (error) {
          // Бэк вернёт 400 с понятным сообщением если есть оформленные подписки
          showToast(error?.message || 'Ошибка удаления', 'error');
        }
        setConfirmDialog(null);
      },
    });
  };

  const handleImageRotate = async (imageId, direction) => {
    if (!editingItem) return;
    try {
      await api.rotateSubscriptionCatalogImage(editingItem.id, imageId, direction);
      setTimeout(async () => {
        try {
          const updated = await api.getSubscriptionCatalog(true);
          const found = updated.find(i => i.id === editingItem.id);
          if (found) setEditingItem({ ...found });
        } catch (e) {}
      }, 300);
    } catch (error) {
      showToast('Ошибка поворота', 'error');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (createTrigger === 'create') {
      openCreateModal();
      if (onCreateHandled) onCreateHandled();
    }
  }, [createTrigger]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const catalog = await api.getSubscriptionCatalog(true);
      setItems(catalog);
    } catch (error) {
      showToast('Ошибка загрузки', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '', is_active: true });
    setEditingItem(null);
    setPendingImages([]);
    setShowModal(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      price: String(item.price),
      is_active: item.is_active,
    });
    setPendingImages([]);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showToast('Введите название', 'error');
      return;
    }
    if (!formData.price || Number(formData.price) <= 0) {
      showToast('Введите цену', 'error');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        price: Number(formData.price),
        is_active: formData.is_active,
      };

      let savedItem;
      if (editingItem) {
        savedItem = await api.updateSubscriptionCatalogItem(editingItem.id, payload);
        showToast('Карточка обновлена');
      } else {
        savedItem = await api.createSubscriptionCatalogItem(payload);
        showToast('Карточка создана');
      }

      // Загружаем ожидающие фото
      for (const file of pendingImages) {
        try {
          await api.uploadSubscriptionCatalogImage(savedItem.id, file);
        } catch (e) {
          showToast(`Ошибка загрузки фото: ${e.message}`, 'error');
        }
      }

      resetForm();
      loadData();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (editingItem) {
      // Загрузка сразу при редактировании
      for (const file of files) {
        try {
          await api.uploadSubscriptionCatalogImage(editingItem.id, file);
          showToast('Фото загружено');
        } catch (error) {
          showToast(error.message, 'error');
        }
      }
      loadData();
      // Обновляем editingItem
      try {
        const updated = await api.getSubscriptionCatalog(true);
        const found = updated.find(i => i.id === editingItem.id);
        if (found) setEditingItem(found);
      } catch (e) {}
    } else {
      // При создании — сохраняем для загрузки после создания
      setPendingImages(prev => [...prev, ...files]);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!editingItem) return;
    try {
      await api.deleteSubscriptionCatalogImage(editingItem.id, imageId);
      showToast('Фото удалено');
      loadData();
      const updated = await api.getSubscriptionCatalog(true);
      const found = updated.find(i => i.id === editingItem.id);
      if (found) setEditingItem(found);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  if (isLoading) {
    return <div style={{ padding: 20, textAlign: 'center' }}>Загрузка...</div>;
  }

  const handleToggleActive = async (item) => {
    try {
      await api.updateSubscriptionCatalogItem(item.id, { is_active: !item.is_active });
      showToast(item.is_active ? 'Карточка скрыта из каталога' : 'Карточка показывается в каталоге');
      loadData();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  return (
    <div>
      {/* Таблица карточек */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>Фото</th>
              <th>Название</th>
              <th style={{ width: 130 }}>Цена</th>
              <th style={{ width: 100, textAlign: 'center' }}>Видна</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: '#5E6561' }}>Нет карточек подписок</td></tr>
            ) : items.map(item => {
              const dimStyle = !item.is_active ? { opacity: 0.5 } : null;
              return (
                <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => openEditModal(item)}>
                  <td style={dimStyle} onClick={e => {
                    if (!item.images?.length) return;
                    e.stopPropagation();
                    setLightbox({
                      images: item.images.map(img => ({ src: `${API_URL}${img.image_url || img.image_large || img.image_medium}` })),
                      index: 0,
                    });
                  }}>
                    {item.images && item.images.length > 0 ? (
                      <img
                        src={`${API_URL}${item.images[0].image_small || item.images[0].image_url}`}
                        alt=""
                        style={{ width: 50, height: 50, objectFit: 'cover', '--ch': '6px', cursor: 'zoom-in' }}
                      />
                    ) : (
                      <div style={{ width: 50, height: 50, background: '#F4F4EF', '--ch': '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5E6561', fontSize: 20 }}>
                        📷
                      </div>
                    )}
                  </td>
                  <td style={dimStyle}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    {item.description && (
                      <div style={{ fontSize: 12, color: '#5E6561', marginTop: 2, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.description}
                      </div>
                    )}
                  </td>
                  <td style={{ ...dimStyle, fontWeight: 700, fontSize: 15, color: '#1C3D2E', whiteSpace: 'nowrap' }}>
                    {item.price.toLocaleString('ru-RU')} ₽
                  </td>
                  <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <Toggle
                      checked={item.is_active}
                      onChange={() => handleToggleActive(item)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Модалка создания/редактирования */}
      {showModal && (
        <Modal
          title={editingItem ? (editingItem.name || 'Без названия') : 'Новая подписка'}
          onClose={resetForm}
          confirmOverlayClose
          footer={
            <>
              {editingItem && (
                <button
                  className="btn btn-danger-outline"
                  onClick={() => handleDelete(editingItem)}
                  style={{ marginRight: 'auto' }}
                >
                  Удалить
                </button>
              )}
              <button className="btn btn-secondary" onClick={resetForm}>Отмена</button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.name.trim() || !formData.price || Number(formData.price) <= 0}
              >
                {editingItem ? 'Сохранить' : 'Создать'}
              </button>
            </>
          }
        >
          {/* Видимость — тумблер сверху */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', background: '#F4F4EF', '--ch': '8px', '--stroke': '#EBEDEC',
            border: '1px solid #EBEDEC', marginBottom: 14,
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1C3D2E' }}>
                {formData.is_active ? 'Видна в каталоге' : 'Скрыта из каталога'}
              </div>
              <div style={{ fontSize: 12, color: '#5E6561', marginTop: 2 }}>
                {formData.is_active ? 'Карточка показывается клиентам' : 'Карточка не показывается клиентам'}
              </div>
            </div>
            <Toggle
              checked={formData.is_active}
              onChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
            />
          </div>

          {/* Фото — компактная горизонтальная лента */}
          <div className="form-group">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {editingItem && editingItem.images && editingItem.images.map((img, idx) => (
                <div key={img.id} style={{ position: 'relative', width: 100, height: 100 }}>
                  <img
                    src={`${API_URL}${img.image_small || img.image_url}?t=${Date.now()}`}
                    alt=""
                    style={{ width: 100, height: 100, objectFit: 'cover', '--ch': '8px', cursor: 'zoom-in' }}
                    onClick={() => setModalLightboxIndex(idx)}
                  />
                  {/* «×» на фото — удаление, красным, не терракотой (UX-аудит 25.10) */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id); }}
                    title="Удалить"
                    style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 22, height: 22, borderRadius: '50%', border: 'none',
                      background: 'rgba(203,77,69, 0.95)', color: '#fff', cursor: 'pointer',
                      fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 2,
                    }}
                  >×</button>
                </div>
              ))}
              {!editingItem && pendingImages.map((file, idx) => (
                <div key={`p-${idx}`} style={{ position: 'relative', width: 100, height: 100 }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt=""
                    style={{ width: 100, height: 100, objectFit: 'cover', '--ch': '8px'}}
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 100, height: 100, border: '2px dashed #D8DDDA', '--ch': '8px', '--stroke': '#D8DDDA', '--bw': '2px',
                  background: '#fff', cursor: 'pointer', fontSize: 32, color: '#1C3D2E',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                }}
                title="Загрузить фото"
              >+</button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Название + Цена в одной строке */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Название <span style={{ color: '#B0603C' }}>*</span></label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Например: Букет S"
              />
            </div>
            <div className="form-group" style={{ width: 130 }}>
              <label className="form-label">Цена (₽) <span style={{ color: '#B0603C' }}>*</span></label>
              <input
                type="number"
                className="form-input"
                value={formData.price}
                onChange={e => setFormData(prev => ({ ...prev, price: e.target.value.replace(/[^\d]/g, '') }))}
                min="1"
                step="1"
                placeholder="0"
              />
            </div>
          </div>

          {/* Описание — auto-grow */}
          <div className="form-group">
            <label className="form-label">Описание</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={e => {
                setFormData(prev => ({ ...prev, description: e.target.value }));
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              ref={el => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }
              }}
              placeholder="Описание для клиента"
              style={{ minHeight: 60, resize: 'none', overflow: 'hidden' }}
            />
          </div>
        </Modal>
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {modalLightboxIndex !== null && editingItem?.images?.length > 0 && (
        <PhotoLightbox
          images={editingItem.images.map(i => ({
            src: `${API_URL}${i.image_url || i.image_large || i.image_medium}?t=${Date.now()}`,
            id: i.id,
          }))}
          startIndex={modalLightboxIndex}
          onClose={() => setModalLightboxIndex(null)}
          onRotate={(image, dir) => handleImageRotate(image.id, dir)}
        />
      )}
    </div>
  );
}

export default SubscriptionCatalogTab;
